import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@common/components/icons/CloseIcon';
import PlayIcon from '@common/components/icons/PlayIcon';
import useModalBehavior from '@common/hooks/useModalBehavior';
import { fetchSavedReportsByDashboard } from '@api/savedReports';
import EnabledStatusBadge from '../EnabledStatusBadge';
import * as styles from './SavedReportsListModal.module.css';

/**
 * SavedReportsListModal Component
 *
 * Modal displaying saved reports for a specific dashboard/report template.
 * Supports two modes:
 * - Interactive mode (default): Card layout with Run button, fetches data from API
 * - Read-only mode: Table layout with status, accepts data as props
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when modal is closed
 * @param {number} dashboardId - The dashboard ID to fetch saved reports for (interactive mode)
 * @param {string} dashboardName - Name of the dashboard for the title
 * @param {boolean} readOnly - If true, shows table view without Run button
 * @param {Array} data - Pre-loaded data for read-only mode (optional)
 * @param {string} title - Custom title override (optional)
 */
export default function SavedReportsListModal({
  isOpen,
  onClose,
  dashboardId,
  dashboardName,
  readOnly,
  data,
  title,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [savedReports, setSavedReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Use shared modal behavior hook for escape key and overlay click handling
  const { handleOverlayClick } = useModalBehavior(isOpen, onClose);

  // Use provided data in read-only mode, or fetch in interactive mode
  useEffect(() => {
    if (!isOpen) return;

    // In read-only mode with provided data, use that directly
    if (readOnly && data) {
      setSavedReports(data);
      return;
    }

    // In interactive mode, fetch from API
    if (!dashboardId) return;

    const loadSavedReports = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchSavedReportsByDashboard(dashboardId);
        if (result.status === 'success') {
          setSavedReports(result.data);
        } else {
          setError(result.message || t('common.error'));
        }
      } catch (err) {
        setError(err.message || t('common.error'));
      } finally {
        setLoading(false);
      }
    };

    loadSavedReports();
  }, [isOpen, dashboardId, readOnly, data, t]);

  // Handle running a saved report (interactive mode only)
  const handleRunReport = useCallback(
    (savedReport) => {
      if (readOnly) return;
      navigate(`/${savedReport.dashboardId}/view`, {
        state: {
          filters: savedReport.configuration || {},
          savedReportId: savedReport.id,
          savedReportName: savedReport.name,
        },
      });
      onClose();
    },
    [navigate, onClose, readOnly],
  );

  // Format date for display
  const formatDate = useCallback((dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }, []);

  // Format date with time for read-only mode
  const formatDateTime = useCallback((dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  if (!isOpen) return null;

  // Determine modal title
  const modalTitle = title
    || (readOnly
      ? t('reports.dependency.savedReportsTitle')
      : t('savedReports.savedReportsFor', { name: dashboardName }));

  return (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        className={`${styles.modal} ${readOnly ? styles.modalWide : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="saved-reports-modal-title"
      >
        {/* Header */}
        <div className={styles.header}>
          <h2 id="saved-reports-modal-title" className={styles.title}>
            {modalTitle}
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Divider */}
        <div className={styles.divider} />

        {/* Content */}
        <div className={styles.content}>
          {loading && (
            <div className={styles.loading}>{t('common.loading')}</div>
          )}

          {error && <div className={styles.error}>{error}</div>}

          {!loading && !error && savedReports.length === 0 && (
            <div className={styles.empty}>{t('savedReports.noSavedReports')}</div>
          )}

          {!loading && !error && savedReports.length > 0 && (
            readOnly ? (
              // Read-only table view
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('common.name')}</th>
                    <th>{t('reports.dependency.columnLastRun')}</th>
                    <th>{t('reports.dependency.columnStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {savedReports.map((report) => (
                    <tr key={report.id}>
                      <td className={styles.nameCell}>{report.name}</td>
                      <td className={styles.dateCell}>
                        {formatDateTime(report.lastRun)}
                      </td>
                      <td>
                        <EnabledStatusBadge
                          status={report.status}
                          namespace="savedReports"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              // Interactive card view
              <div className={styles.reportsList}>
                {savedReports.map((report) => (
                  <div key={report.id} className={styles.reportCard}>
                    <div className={styles.reportInfo}>
                      <h3 className={styles.reportName}>{report.name}</h3>
                      <p className={styles.reportDescription}>
                        {report.description || t('reports.noDescription')}
                      </p>
                      <span className={styles.reportDate}>
                        {t('savedReports.created')}: {formatDate(report.createdAt)}
                      </span>
                    </div>
                    <button
                      type="button"
                      className={styles.runButton}
                      onClick={() => handleRunReport(report)}
                    >
                      {t('savedReports.run')}
                      <PlayIcon />
                    </button>
                  </div>
                ))}
              </div>
            )
          )}
        </div>

        {/* Bottom divider */}
        <div className={styles.divider} />

        {/* Footer spacer */}
        <div className={styles.footerSpacer} />
      </div>
    </div>
  );
}

SavedReportsListModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dashboardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dashboardName: PropTypes.string,
  readOnly: PropTypes.bool,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
      lastRun: PropTypes.string,
      status: PropTypes.number,
      createdAt: PropTypes.string,
    }),
  ),
  title: PropTypes.string,
};

SavedReportsListModal.defaultProps = {
  dashboardId: null,
  dashboardName: 'Report',
  readOnly: false,
  data: null,
  title: null,
};
